import { useMemo, useState } from 'react'
import { Archive, Edit3, Plus, RotateCcw, Save, X } from 'lucide-react'
import { Icon } from '../../components/Icon'
import { categoryLabel, formatMoney } from '../../lib/format'
import { useAppStore } from '../../store/MockAppStore'
import type { Category, MenuItem, MenuItemDraft } from '../../types'

const iconOptions = [
  ['coffee', 'Coffee'], ['sparkles', 'Sparkles'], ['droplets', 'Water'], ['refresh', 'Refill'],
  ['moon', 'Quiet'], ['pause', 'Break'], ['heart', 'Care'], ['sliders', 'Adjust'],
  ['cloud', 'Steam'], ['waves', 'Massage'], ['leaf', 'Botanical'], ['bell', 'Request'], ['price', 'Paid item'],
] as const

const categories: Category[] = ['amenity', 'comfort', 'upgrade']

interface EditorState {
  id?: string
  draft: MenuItemDraft
  paid: boolean
  price: string
  confirmingArchive: boolean
}

const editorFor = (item: MenuItem): EditorState => ({
  id: item.id,
  draft: {
    name: item.name,
    description: item.description,
    category: item.category,
    priceCents: item.priceCents,
    enabled: item.enabled,
    icon: item.icon,
    sortOrder: item.sortOrder,
  },
  paid: item.priceCents > 0,
  price: item.priceCents > 0 ? (item.priceCents / 100).toFixed(item.priceCents % 100 ? 2 : 0) : '',
  confirmingArchive: false,
})

export function MenuManager() {
  const { menu, createMenuItem, updateMenuItem, archiveMenuItem } = useAppStore()
  const [editor, setEditor] = useState<EditorState | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const activeItems = useMemo(() => menu.filter((item) => !item.archived), [menu])
  const archivedItems = useMemo(() => menu.filter((item) => item.archived), [menu])

  const openNew = () => {
    const nextOrder = Math.max(0, ...activeItems.map((item) => item.sortOrder)) + 10
    setError('')
    setEditor({
      draft: { name: '', description: '', category: 'amenity', priceCents: 0, enabled: true, icon: 'sparkles', sortOrder: nextOrder },
      paid: false,
      price: '',
      confirmingArchive: false,
    })
  }

  const save = async () => {
    if (!editor) return
    const price = editor.paid ? Number(editor.price) : 0
    if (!editor.draft.name.trim()) return setError('Enter an item name.')
    if (editor.paid && (!Number.isFinite(price) || price <= 0 || price > 1000)) return setError('Enter a paid price between $0.01 and $1,000.')
    const priceCents = editor.paid ? Math.round(price * 100) : 0
    setSaving(true)
    setError('')
    try {
      const draft = { ...editor.draft, name: editor.draft.name.trim(), description: editor.draft.description.trim(), priceCents }
      if (editor.id) await updateMenuItem(editor.id, draft)
      else await createMenuItem(draft)
      setEditor(null)
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'The menu item could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  const archive = async () => {
    if (!editor?.id) return
    setSaving(true)
    setError('')
    try {
      await archiveMenuItem(editor.id)
      setEditor(null)
    } catch (archiveError) {
      setError(archiveError instanceof Error ? archiveError.message : 'The menu item could not be archived.')
    } finally {
      setSaving(false)
    }
  }

  const quickAvailability = (item: MenuItem, enabled: boolean) => {
    setError('')
    void updateMenuItem(item.id, { enabled }).catch((updateError) => {
      setError(updateError instanceof Error ? updateError.message : 'Availability could not be changed.')
    })
  }

  const restore = (item: MenuItem) => {
    setError('')
    void updateMenuItem(item.id, { archived: false, enabled: false }).catch((updateError) => {
      setError(updateError instanceof Error ? updateError.message : 'The menu item could not be restored.')
    })
  }

  return (
    <div className="staff-view menu-manager">
      <div className="staff-view__heading menu-manager__heading">
        <div><p className="eyebrow">Client offerings</p><h1>Concierge menu</h1><p>Add, organize, price, and publish offerings without a redeploy.</p></div>
        <button className="staff-add-button" onClick={openNew}><Plus size={18} />Add Menu Item</button>
      </div>
      {error && !editor && <p className="staff-form-error" role="alert">{error}</p>}

      {categories.map((category) => {
        const items = activeItems.filter((item) => item.category === category)
        return (
          <section className="manager-category" key={category} aria-labelledby={`manager-${category}`}>
            <div className="manager-category__heading"><h2 id={`manager-${category}`}>{categoryLabel[category]}</h2><span>{items.length}</span></div>
            <div className="manager-list">
              {items.map((item) => (
                <article className="manager-row" key={item.id}>
                  <span className={`item-icon item-icon--${item.category}`}><Icon name={item.icon} /></span>
                  <div className="manager-row__copy"><span>Order {item.sortOrder} · {item.priceCents > 0 ? 'Paid' : 'Complimentary'}</span><strong>{item.name}</strong><p>{item.description}</p></div>
                  {item.priceCents > 0 && <b>{formatMoney(item.priceCents)}</b>}
                  <label className="switch" aria-label={`${item.enabled ? 'Disable' : 'Enable'} ${item.name}`}><input type="checkbox" checked={item.enabled} onChange={(event) => quickAvailability(item, event.target.checked)} /><span /></label>
                  <button className="icon-button" onClick={() => { setError(''); setEditor(editorFor(item)) }} aria-label={`Edit ${item.name}`}><Edit3 size={17} /></button>
                </article>
              ))}
              {!items.length && <p className="manager-empty">No items in this category yet.</p>}
            </div>
          </section>
        )
      })}

      {archivedItems.length > 0 && (
        <details className="archive-list">
          <summary>Archived items <span>{archivedItems.length}</span></summary>
          <div className="manager-list">
            {archivedItems.map((item) => (
              <article className="manager-row manager-row--archived" key={item.id}>
                <span className={`item-icon item-icon--${item.category}`}><Icon name={item.icon} /></span>
                <div className="manager-row__copy"><span>{categoryLabel[item.category]}</span><strong>{item.name}</strong><p>Hidden from the client menu. Historical request prices are preserved.</p></div>
                {item.priceCents > 0 && <b>{formatMoney(item.priceCents)}</b>}
                <button className="restore-button" onClick={() => restore(item)}><RotateCcw size={15} />Restore</button>
              </article>
            ))}
          </div>
        </details>
      )}

      {editor && (
        <div className="sheet-backdrop menu-editor-backdrop" onMouseDown={(event) => event.target === event.currentTarget && !saving && setEditor(null)}>
          <section className="edit-dialog menu-editor" role="dialog" aria-modal="true" aria-labelledby="edit-title">
            <div className="sheet-grabber" aria-hidden="true" />
            <div className="dialog-head"><div><p className="eyebrow">{editor.id ? 'Edit offering' : 'New offering'}</p><h2 id="edit-title">{editor.id ? 'Menu details' : 'Add menu item'}</h2></div><button className="icon-button" onClick={() => setEditor(null)} disabled={saving} aria-label="Close"><X size={18} /></button></div>
            <div className="menu-editor__body">
              <label className="field-label" htmlFor="edit-name">Item name</label>
              <input id="edit-name" maxLength={100} value={editor.draft.name} onChange={(event) => setEditor({ ...editor, draft: { ...editor.draft, name: event.target.value } })} />

              <label className="field-label" htmlFor="edit-description">Description</label>
              <textarea id="edit-description" maxLength={300} rows={3} value={editor.draft.description} onChange={(event) => setEditor({ ...editor, draft: { ...editor.draft, description: event.target.value } })} />

              <div className="menu-editor__grid">
                <div><label className="field-label" htmlFor="edit-category">Category</label><select id="edit-category" value={editor.draft.category} onChange={(event) => setEditor({ ...editor, draft: { ...editor.draft, category: event.target.value as Category } })}>{categories.map((category) => <option key={category} value={category}>{categoryLabel[category]}</option>)}</select></div>
                <div><label className="field-label" htmlFor="edit-icon">Icon</label><select id="edit-icon" value={editor.draft.icon} onChange={(event) => setEditor({ ...editor, draft: { ...editor.draft, icon: event.target.value } })}>{iconOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div>
              </div>

              <fieldset className="price-choice"><legend>Pricing</legend><div><button type="button" className={!editor.paid ? 'active' : ''} onClick={() => setEditor({ ...editor, paid: false, price: '' })}>Complimentary</button><button type="button" className={editor.paid ? 'active' : ''} onClick={() => setEditor({ ...editor, paid: true })}>Paid</button></div></fieldset>
              {editor.paid && <><label className="field-label" htmlFor="edit-price">Price (USD)</label><div className="price-input"><span>$</span><input id="edit-price" inputMode="decimal" type="number" min="0.01" max="1000" step="0.01" placeholder="0.00" value={editor.price} onChange={(event) => setEditor({ ...editor, price: event.target.value })} /></div><p className="field-help">Clients must acknowledge this price. Requests remain subject to your approval and are never charged here.</p></>}

              <div className="menu-editor__grid menu-editor__grid--bottom">
                <div><label className="field-label" htmlFor="edit-order">Display order</label><input id="edit-order" type="number" inputMode="numeric" min="0" max="10000" step="1" value={editor.draft.sortOrder} onChange={(event) => setEditor({ ...editor, draft: { ...editor.draft, sortOrder: Math.max(0, Math.round(Number(event.target.value) || 0)) } })} /></div>
                <label className="availability-control"><span><strong>Available now</strong><small>Show on client menu</small></span><span className="switch"><input type="checkbox" checked={editor.draft.enabled} onChange={(event) => setEditor({ ...editor, draft: { ...editor.draft, enabled: event.target.checked } })} /><span /></span></label>
              </div>

              {error && <p className="form-error" role="alert">{error}</p>}
              {editor.id && (editor.confirmingArchive ? <div className="archive-confirm"><p><strong>Archive this item?</strong> It will disappear from the client menu, while every historical request and price snapshot stays intact.</p><div><button type="button" onClick={() => setEditor({ ...editor, confirmingArchive: false })}>Keep item</button><button type="button" className="danger-button" onClick={archive} disabled={saving}>Archive item</button></div></div> : <button type="button" className="archive-button" onClick={() => setEditor({ ...editor, confirmingArchive: true })}><Archive size={16} />Archive (safe delete)</button>)}
            </div>
            <div className="menu-editor__footer"><button className="secondary-button" onClick={() => setEditor(null)} disabled={saving}>Cancel</button><button className="primary-button" onClick={save} disabled={saving || !editor.draft.name.trim()}>{saving ? <span className="spinner" /> : <Save size={17} />}{editor.id ? 'Save changes' : 'Add item'}</button></div>
          </section>
        </div>
      )}
    </div>
  )
}
